import React from 'react'
import PropTypes from 'prop-types'
import { Typography, Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { ArrowDownward as ArrowDownIcon, ArrowBack as ArrowBackIcon } from '@material-ui/icons'
import strings from 'strings'
import {
  ACCENT_ORANGE,
  ACCENT_PURPLE,
  BANNER_BG,
  NOT_REPORTED,
  changeOf,
  deriveComparisonVerdict,
  formatDayLabel,
  formatOptionalCurrency,
  formatOptionalPercent,
  formatPeriodLabel,
  formatPeriodRange,
  formatPoints,
  formatSignedCurrency,
  formatSignedPercent,
  getStatusStyle,
  getHealthStatusLabel,
  isReported,
  readMetrics,
  splitFlag
} from './incomeStatementAdapters'

// Margins are compared in percentage points; amounts in currency and percent change.
const CHANGE_METRICS = [
  { key: 'totalRevenueChange', labelKey: 'INCOME_STATEMENT_TOTAL_REVENUE', read: (m) => m.totalRevenue, format: formatOptionalCurrency, points: false, color: BANNER_BG },
  { key: 'grossProfitChange', labelKey: 'INCOME_STATEMENT_GROSS_PROFIT', read: (m) => m.grossProfit, format: formatOptionalCurrency, points: false, color: ACCENT_PURPLE },
  { key: 'operatingIncomeChange', labelKey: 'INCOME_STATEMENT_OPERATING_INCOME', read: (m) => m.operatingIncome, format: formatOptionalCurrency, points: false, color: ACCENT_PURPLE },
  { key: 'netIncomeChange', labelKey: 'INCOME_STATEMENT_NET_INCOME', read: (m) => m.netIncome, format: formatOptionalCurrency, points: false, color: BANNER_BG },
  { key: 'grossMarginChange', labelKey: 'INCOME_STATEMENT_GROSS_MARGIN_UPPER', read: (m) => m.grossMargin, format: formatOptionalPercent, points: true, color: ACCENT_ORANGE },
  { key: 'netMarginChange', labelKey: 'INCOME_STATEMENT_NET_MARGIN_UPPER', read: (m) => m.netMargin, format: formatOptionalPercent, points: true, color: ACCENT_ORANGE }
]

const useStyles = makeStyles((theme) => ({
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2)
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: '#555'
  },
  banner: {
    backgroundColor: BANNER_BG,
    borderRadius: '14px',
    padding: theme.spacing(4),
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  bannerEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: ACCENT_PURPLE,
    marginBottom: theme.spacing(1)
  },
  bannerHeadline: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  bannerDescription: {
    fontSize: 14,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.78)',
    maxWidth: 560
  },
  scorePairBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '10px',
    padding: theme.spacing(2),
    alignSelf: 'flex-start'
  },
  scoreBoxLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: 'rgba(255,255,255,0.6)'
  },
  scoreBoxValue: {
    fontSize: 26,
    fontWeight: 700
  },
  flagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  flagCard: {
    flex: '1 1 260px',
    minWidth: 240,
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  flagEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: ACCENT_ORANGE,
    marginBottom: 6
  },
  flagTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4
  },
  flagBody: {
    fontSize: 12,
    color: '#666',
    lineHeight: 1.5
  },
  flagBar: {
    height: 3,
    borderRadius: 2,
    backgroundColor: ACCENT_ORANGE,
    marginTop: theme.spacing(2)
  },
  deltaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  deltaCard: {
    flex: '1 1 180px',
    minWidth: 170,
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  deltaLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'var(--neutral-400)'
  },
  deltaValue: {
    fontSize: 20,
    fontWeight: 700,
    margin: '6px 0 4px'
  },
  deltaSubtext: {
    fontSize: 11,
    color: '#777'
  },
  deltaBarTrack: {
    height: 3,
    backgroundColor: '#EFEFEF',
    borderRadius: 2,
    marginTop: theme.spacing(1.5),
    overflow: 'hidden'
  },
  deltaBarFill: {
    height: '100%',
    borderRadius: 2
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: 12,
    color: 'var(--neutral-400)',
    marginTop: 2
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#777'
  },
  legendSwatch: {
    width: 8,
    height: 8,
    borderRadius: 2
  },
  popRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  popGroup: {
    flex: '1 1 180px',
    minWidth: 160
  },
  popPlot: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    height: 130
  },
  popBarSlot: {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  },
  popValue: {
    fontSize: 10,
    color: '#777',
    marginBottom: 4
  },
  popBar: {
    borderRadius: '3px 3px 0 0',
    minHeight: 3
  },
  popMetricLabel: {
    fontSize: 11,
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(1)
  },
  shiftRow: {
    marginTop: theme.spacing(2.5)
  },
  shiftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    marginBottom: 6
  },
  shiftTrack: {
    height: 6,
    backgroundColor: '#F3F3F3',
    borderRadius: 3,
    marginBottom: 3,
    overflow: 'hidden'
  },
  shiftFill: {
    height: '100%',
    borderRadius: 3
  },
  analysisRow: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: `${theme.spacing(1.5)}px 0`,
    borderBottom: '1px solid #F0F0F0'
  },
  analysisIndex: {
    fontSize: 11,
    fontWeight: 700,
    color: '#c4c4c4',
    marginTop: 2
  },
  analysisText: {
    fontSize: 13,
    lineHeight: 1.6
  }
}))

const deltaHeadline = (delta) => {
  if (!delta.change) return NOT_REPORTED
  if (delta.points) return formatPoints(delta.change.absolute)
  if (delta.change.percent === null) return formatSignedCurrency(delta.change.absolute)
  if (delta.change.percent === 0) return strings.INCOME_STATEMENT_UNCHANGED
  return formatSignedPercent(delta.change.percent)
}

const deltaColor = (delta) => {
  if (!delta.change || delta.change.absolute === 0) return '#555'
  return delta.change.absolute > 0 ? 'var(--status-approved)' : ACCENT_ORANGE
}

const IncomeStatementComparison = ({ fromDoc, toDoc, comparisonData, onBack }) => {
  const classes = useStyles()
  const report = comparisonData ? comparisonData.report : null

  if (!report) {
    return (
      <Paper className={classes.sectionCard}>
        <div className={classes.topBar}>
          <div className={classes.backLink} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> {strings.INCOME_STATEMENT_BACK}
          </div>
        </div>
        <Typography>{strings.INCOME_STATEMENT_NO_COMPARISON_DATA}</Typography>
      </Paper>
    )
  }

  const fromMetrics = readMetrics(fromDoc)
  const toMetrics = readMetrics(toDoc)
  const fromLabel = comparisonData.date1 ? formatPeriodLabel(comparisonData.date1) : formatDayLabel(fromMetrics.periodEnd)
  const toLabel = comparisonData.date2 ? formatPeriodLabel(comparisonData.date2) : formatDayLabel(toMetrics.periodEnd)
  const verdict = deriveComparisonVerdict(report, fromMetrics, toMetrics)
  const triggeredFlags = report.triggeredFlags || []
  const takeaways = report.takeawayAnalysis || []

  const deltas = CHANGE_METRICS.map((metric) => {
    const fromValue = metric.read(fromMetrics)
    const toValue = metric.read(toMetrics)
    return {
      ...metric,
      fromValue,
      toValue,
      change: changeOf(report, metric.key, fromValue, toValue)
    }
  })

  const periodMetrics = [
    { label: strings.INCOME_STATEMENT_TOTAL_REVENUE_TITLE, from: fromMetrics.totalRevenue, to: toMetrics.totalRevenue },
    { label: strings.INCOME_STATEMENT_GROSS_PROFIT_LABEL, from: fromMetrics.grossProfit, to: toMetrics.grossProfit },
    { label: strings.INCOME_STATEMENT_OPERATING_INCOME_LABEL, from: fromMetrics.operatingIncome, to: toMetrics.operatingIncome },
    { label: strings.INCOME_STATEMENT_NET_INCOME_LABEL, from: fromMetrics.netIncome, to: toMetrics.netIncome }
  ].filter((metric) => isReported(metric.from) || isReported(metric.to)).map((metric) => {
    const scale = Math.max(Math.abs(metric.from || 0), Math.abs(metric.to || 0), 1)
    return {
      ...metric,
      fromPct: (Math.abs(metric.from || 0) / scale) * 100,
      toPct: (Math.abs(metric.to || 0) / scale) * 100
    }
  })

  const marginRows = [
    { label: strings.INCOME_STATEMENT_GROSS_MARGIN, color: BANNER_BG, from: fromMetrics.grossMargin, to: toMetrics.grossMargin },
    { label: strings.INCOME_STATEMENT_OPERATING_MARGIN, color: ACCENT_PURPLE, from: fromMetrics.operatingMargin, to: toMetrics.operatingMargin },
    { label: strings.INCOME_STATEMENT_NET_MARGIN, color: ACCENT_ORANGE, from: fromMetrics.netMargin, to: toMetrics.netMargin }
  ].filter((row) => isReported(row.from) || isReported(row.to))

  const fromStatusStyle = getStatusStyle(getHealthStatusLabel(fromMetrics.healthScore))
  const toStatusStyle = getStatusStyle(getHealthStatusLabel(toMetrics.healthScore))

  return (
    <div>
      <div className={classes.topBar}>
        <div className={classes.backLink} onClick={onBack}>
          <ArrowBackIcon fontSize="small" /> {strings.INCOME_STATEMENT_BACK}
        </div>
      </div>

      {!verdict.comparable && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          {verdict.comparabilityNote || strings.INCOME_STATEMENT_PERIODS_NOT_COMPARABLE}
        </Alert>
      )}

      <div className={classes.banner}>
        <div>
          <Typography className={classes.bannerEyebrow}>
            {formatPeriodRange(fromMetrics.periodStart, fromMetrics.periodEnd).toUpperCase()} → {formatPeriodRange(toMetrics.periodStart, toMetrics.periodEnd).toUpperCase()}
          </Typography>
          <Typography className={classes.bannerHeadline}>{verdict.headline}</Typography>
          <Typography className={classes.bannerDescription}>{verdict.description}</Typography>
        </div>
        <div className={classes.scorePairBox}>
          <div>
            <Typography className={classes.scoreBoxLabel}>{fromLabel}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: fromStatusStyle.accent }}>
              {fromMetrics.healthScore.toFixed(1)}
            </Typography>
          </div>
          <ArrowDownIcon
            style={{
              color: verdict.improved ? '#4caf82' : ACCENT_ORANGE,
              transform: verdict.improved ? 'rotate(180deg)' : 'none'
            }}
          />
          <div>
            <Typography className={classes.scoreBoxLabel}>{toLabel}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: toStatusStyle.accent }}>
              {toMetrics.healthScore.toFixed(1)}
            </Typography>
          </div>
        </div>
      </div>

      {triggeredFlags.length > 0 && (
        <div className={classes.flagsRow}>
          {triggeredFlags.map((flag, index) => {
            const { title, body } = splitFlag(flag)
            return (
              <Paper className={classes.flagCard} key={flag}>
                <Typography className={classes.flagEyebrow}>
                  {strings.INCOME_STATEMENT_TRIGGERED_FLAGS.toUpperCase()} · {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography className={classes.flagTitle}>{title}</Typography>
                <Typography className={classes.flagBody}>{body}</Typography>
                <div className={classes.flagBar} />
              </Paper>
            )
          })}
        </div>
      )}

      <div className={classes.deltaRow}>
        {deltas.map((delta) => (
          <Paper className={classes.deltaCard} key={delta.key}>
            <Typography className={classes.deltaLabel}>{strings[delta.labelKey]}</Typography>
            <Typography className={classes.deltaValue} style={{ color: deltaColor(delta) }}>
              {deltaHeadline(delta)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {delta.format(delta.fromValue)} → {delta.format(delta.toValue)}
            </Typography>
            {delta.change && !delta.points && delta.change.absolute !== 0 && (
              <Typography className={classes.deltaSubtext}>
                {formatSignedCurrency(delta.change.absolute)}
              </Typography>
            )}
            <div className={classes.deltaBarTrack}>
              <div
                className={classes.deltaBarFill}
                style={{
                  width: `${Math.max(2, Math.min(100, Math.abs((delta.change && (delta.points ? delta.change.absolute : delta.change.percent)) || 0)))}%`,
                  backgroundColor: delta.color
                }}
              />
            </div>
          </Paper>
        ))}
      </div>

      {periodMetrics.length > 0 && (
        <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
          <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_PERIOD_OVER_PERIOD}</Typography>
          <Typography className={classes.sectionSubtitle}>{strings.INCOME_STATEMENT_PERIOD_OVER_PERIOD_SUBTITLE}</Typography>

          <div className={classes.legend}>
            <span className={classes.legendItem}>
              <span className={classes.legendSwatch} style={{ backgroundColor: BANNER_BG }} />
              {strings.INCOME_STATEMENT_PREVIOUS} · {fromLabel}
            </span>
            <span className={classes.legendItem}>
              <span className={classes.legendSwatch} style={{ backgroundColor: ACCENT_PURPLE }} />
              {strings.INCOME_STATEMENT_CURRENT} · {toLabel}
            </span>
          </div>

          <div className={classes.popRow}>
            {periodMetrics.map((metric) => (
              <div className={classes.popGroup} key={metric.label}>
                <div className={classes.popPlot}>
                  <div className={classes.popBarSlot}>
                    <Typography className={classes.popValue}>{formatOptionalCurrency(metric.from)}</Typography>
                    <div
                      className={classes.popBar}
                      style={{ height: `${metric.fromPct}%`, backgroundColor: isReported(metric.from) && metric.from < 0 ? ACCENT_ORANGE : BANNER_BG }}
                    />
                  </div>
                  <div className={classes.popBarSlot}>
                    <Typography className={classes.popValue}>{formatOptionalCurrency(metric.to)}</Typography>
                    <div
                      className={classes.popBar}
                      style={{ height: `${metric.toPct}%`, backgroundColor: isReported(metric.to) && metric.to < 0 ? ACCENT_ORANGE : ACCENT_PURPLE }}
                    />
                  </div>
                </div>
                <Typography className={classes.popMetricLabel}>{metric.label}</Typography>
              </div>
            ))}
          </div>
        </Paper>
      )}

      {marginRows.length > 0 && (
        <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
          <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_WHERE_MARGINS_MOVED}</Typography>
          <Typography className={classes.sectionSubtitle}>
            {strings.INCOME_STATEMENT_WHERE_MARGINS_MOVED_SUBTITLE}, {fromLabel} → {toLabel}
          </Typography>
          {marginRows.map((row) => (
            <div className={classes.shiftRow} key={row.label}>
              <div className={classes.shiftHeader}>
                <span>{row.label}</span>
                <span style={{ color: 'var(--neutral-400)' }}>{formatOptionalPercent(row.from)} → {formatOptionalPercent(row.to)}</span>
              </div>
              <div className={classes.shiftTrack}>
                <div
                  className={classes.shiftFill}
                  style={{ width: `${Math.max(0, Math.min(100, row.from || 0))}%`, backgroundColor: row.color, opacity: 0.45 }}
                />
              </div>
              <div className={classes.shiftTrack}>
                <div
                  className={classes.shiftFill}
                  style={{ width: `${Math.max(0, Math.min(100, row.to || 0))}%`, backgroundColor: row.color }}
                />
              </div>
            </div>
          ))}
        </Paper>
      )}

      {/* The in-document comparison is computed in Java without a model pass, so it carries no takeaway narrative */}
      {takeaways.length > 0 && (
        <Paper className={classes.sectionCard}>
          <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_TAKEAWAY_ANALYSIS}</Typography>
          {takeaways.map((line, index) => (
            <div className={classes.analysisRow} key={line}>
              <Typography className={classes.analysisIndex}>{String(index + 1).padStart(2, '0')}</Typography>
              <Typography className={classes.analysisText}>{line}</Typography>
            </div>
          ))}
        </Paper>
      )}
    </div>
  )
}

IncomeStatementComparison.propTypes = {
  fromDoc: PropTypes.object.isRequired,
  toDoc: PropTypes.object.isRequired,
  comparisonData: PropTypes.object,
  onBack: PropTypes.func.isRequired
}

export default IncomeStatementComparison
